import React, { useRef, useMemo, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import FlexboxGrid from "rsuite/es/FlexboxGrid";
import { Icon, Whisper, Tooltip } from "rsuite";
import styled from "styled-components";
import { ServiceLink } from "components/Navigation/ServiceLink";
import {
    SMS_PRICE_RANGE_NUMBER_REVOKE_LIST_API,
    SMS_PRICE_RANGE_NUMBER_ALLOCATE_API,
    SMS_PRICE_RANGE_NUMBER_SET_TEST_NUMBER_LIST_API,
    SMS_PRICE_RANGE_NUMBER_UNSET_TEST_NUMBER_LIST_API,
    SMS_PRICE_RANGE_NUMBER_SET_BLOCK_ALLOCATION_LIST_API,
    SMS_PRICE_RANGE_NUMBER_UNSET_BLOCK_ALLOCATION_LIST_API
} from "const/apiMethods";
import { LS_RANGES_AND_NUMBERS_NUMBERS_PER_PAGE } from "const/localStorageKeys";
import { checkPermissionsFor } from "store/storeHelpers";
import { getRangeByNumber } from "utils";
import TableServerSort from "components/Table/TableServerSort";
import Unblock from "pages/RangesAndNumbers/Numbers/Unblock";
import Block from "pages/RangesAndNumbers/Numbers/Block";
import SetOrUnsetTest from "./SetOrUnsetTest";
import Allocate from "./Allocate/Allocate";
import Filters from "./NumbersFilters";
import Revoke from "./Revoke";
import "./Numbers.css";


export default forwardRef((
    {
        sort,
        pr_key,
        sde_key,
        service,
        filters,
        numbers,
        account_id,
        numbersPage,
        rangesFilter,

        roleList,

        numbersCount,
        numbersPerPage,
        numbersLoading,
        incorporatedOnly,
        showReasonHandler,
        allocatedNumbersCount,

        onSort,
        getNumbers,
        updateRanges,
        selectedRange,
        setCurrentTrunkId,
        setCurrentRangeNumber,
        onChangeNumberFilters
    },
    ref
) => {

    const [selected, setSelected] = useState({ all: false, list: [] });
    const [selectedNumbersInfo, setSelectedNumbersInfo] = useState([]);

    const [columns, setColumns] = useState([]);

    let tableRef = useRef(null);

    const unitedFilters = useMemo(() => {
        return {
            ...filters,
            show_only_incorporated_numbers: rangesFilter?.only_with_incorporated_numbers
        }
    }, [filters, rangesFilter]);

    useImperativeHandle(ref, () => ({
        clearSelected: () => {
            clearSelected();
        }
    }));

    useEffect(() => {
        const isRange = numbers.some(item => item.numbers_count > 1);

        const changableKey = `key-${isRange}-${service}-${numbersLoading}-${incorporatedOnly}`;

        setColumns([
            {
                label: "Number",
                dataKey: "number",
                value: (({ number, numbers_count, incorporated_group, supplier_gan_group }) => {

                    const group = service ? incorporated_group : supplier_gan_group;

                    if (!group) {
                        return getRangeByNumber(number, numbers_count);
                    }

                    const { rate, cur_name, supplier_rate, supplier_cur_name } = group;

                    const groupRate = service ? rate : supplier_rate;
                    const groupCurName = service ? cur_name : supplier_cur_name;

                    const payoutGroups = group.rate_list
                        .reduce((summ, item) => {
                            return {
                                ...summ,
                                [item.cur_name]: summ[item.cur_name]
                                    ? summ[item.cur_name] + ` ${item.rate}/${item.payment_terms_name}`
                                    : `${item.rate}/${item.payment_terms_name}`
                            }
                        }, {});

                    return (
                        <>
                            {getRangeByNumber(number, numbers_count)}
                            {group &&
                                <Whisper
                                    enterable
                                    placement="right"
                                    speaker={
                                        <Tooltip>
                                            <StyledTooltipInner>
                                                <p>Supplier account name: <br />{group.supplier_name}</p>
                                                <p>Group name: <br />{group.name}</p>
                                                <p>Rate: <br />{groupRate} {groupCurName}</p>
                                                <p>Default payouts: <br />
                                                    {Object.keys(payoutGroups).map((item) => (
                                                        <><b>{item}</b>: {payoutGroups[item]} <br /></>
                                                    ))}
                                                </p>
                                            </StyledTooltipInner>
                                        </Tooltip>
                                    }
                                >
                                    <StyledTooltipIcon icon="info" />
                                </Whisper>
                            }
                        </>
                    );
                }),
                sortable: true,
                width: isRange ? 260 : 200
            },
            {
                key: changableKey,
                label: "Allocated to and trunk",
                dataKey: "trunk_name",
                value: (({
                    account_name, account_manager_name, trunk_name, number, account_id, trunk_id, test_number, rate, currency_name
                }) => {
                    const textResult = account_name && !numbersLoading ?
                        <>
                            <ServiceLink className="ranges_numbers_table_link" to={`/accounts/view/${account_id}`} onClick={() => {
                                setCurrentTrunkId(trunk_id);
                                setCurrentRangeNumber(number)
                            }}>
                                {account_name} {!test_number && `/ ${account_manager_name || "no manager"}`}&nbsp;
                            </ServiceLink>
                            / <AdditionalInfo>{trunk_name} {rate} {currency_name}</AdditionalInfo>
                        </>
                        : trunk_name;

                    return textResult;
                }),
                sortable: true,
                flexGrow: 1
            },
        ]);

        if (numbers.length) {
            clearSelected();
        }
    }, [service, numbers, numbersLoading, incorporatedOnly]);


    const getSelectedList = () => ({
        ...selected,
        countAll: allocatedNumbersCount
    });

    const getNumberList = (filterValue, page, per_page, sort) => {
        getNumbers(filterValue, page, per_page, sort);
    };

    const onChangeFilters = (filters) => {
        const currentFilters = {
            ...filters,
            show_only_incorporated_numbers: rangesFilter?.only_with_incorporated_numbers
        };

        onChangeNumberFilters(currentFilters);
        getNumberList(currentFilters, 1, numbersPerPage, sort);
        clearSelected();
    };

    const clearSelected = () => {
        setSelected({ all: false, list: [] });
        setSelectedNumbersInfo([]);
        tableRef.current.clear();
    };

    const includedNumber = selected.list.length && numbers.filter(number => selected.list.includes(number.prn_key));
    const disabledBlockedNumbersButtonSign = includedNumber && includedNumber.some(number => number.trunk_name === "blocked");
    const hasNoZeroPrefixNumbers = includedNumber && includedNumber.some(number => number.numbers_count !== 1);

    const hasSelected = (selected.list && selected.list.length) || selected.all;
    const isSupervisorSms = roleList && roleList.length ? roleList.includes("Supervisor (sms)") : null;
    const showRevokeButton = !isSupervisorSms || isSupervisorSms && checkPermissionsFor(SMS_PRICE_RANGE_NUMBER_REVOKE_LIST_API);
    const showAllocateButton = !isSupervisorSms || isSupervisorSms && checkPermissionsFor(SMS_PRICE_RANGE_NUMBER_ALLOCATE_API);
    const showSetTestButton = !isSupervisorSms || isSupervisorSms && checkPermissionsFor(SMS_PRICE_RANGE_NUMBER_SET_TEST_NUMBER_LIST_API);
    const showUnsetTestButton = !isSupervisorSms || isSupervisorSms && checkPermissionsFor(SMS_PRICE_RANGE_NUMBER_UNSET_TEST_NUMBER_LIST_API);
    const showBlockButton = !isSupervisorSms || isSupervisorSms && checkPermissionsFor(SMS_PRICE_RANGE_NUMBER_SET_BLOCK_ALLOCATION_LIST_API);
    const showUnblockButton = !isSupervisorSms || isSupervisorSms && checkPermissionsFor(SMS_PRICE_RANGE_NUMBER_UNSET_BLOCK_ALLOCATION_LIST_API);

    const noButtons = !showRevokeButton && !showAllocateButton && !showSetTestButton
        && !showUnsetTestButton && !showBlockButton && !showUnblockButton;

    return (<>
        <FlexboxGrid justify="space-around" style={{ marginBottom: 25, marginTop: noButtons ? 43 : 0, textAlign: "right" }}>
            {showRevokeButton && <FlexboxGrid.Item>
                <Revoke
                    incorporatedOnly={incorporatedOnly}
                    selectedNumbersInfo={selectedNumbersInfo}
                    disabled={!hasSelected}
                    pr_key={pr_key}
                    service={service}
                    filters={unitedFilters}
                    getList={getSelectedList}
                    update={() => {
                        updateRanges();
                        getNumberList(unitedFilters, 1, numbersPerPage, sort);
                        tableRef.current.clear();
                    }}
                />
            </FlexboxGrid.Item>}
            {showAllocateButton && <FlexboxGrid.Item>
                <Allocate
                    showReasonHandler={showReasonHandler}
                    incorporatedOnly={incorporatedOnly}
                    selectedNumbersInfo={selectedNumbersInfo}
                    service={service}
                    filters={unitedFilters}
                    disabled={!hasSelected || disabledBlockedNumbersButtonSign}
                    rangesFilter={rangesFilter}
                    getList={getSelectedList}
                    pr_key={pr_key}
                    update={() => {
                        updateRanges();
                        getNumberList(unitedFilters, 1, numbersPerPage, sort);
                        tableRef.current.clear();
                    }}
                    account_id={account_id}
                    sde_key={sde_key}
                />
            </FlexboxGrid.Item>}
            {showSetTestButton && <FlexboxGrid.Item>
                <SetOrUnsetTest
                    incorporatedOnly={incorporatedOnly}
                    selectedNumbersInfo={selectedNumbersInfo}
                    disabled={!hasSelected || disabledBlockedNumbersButtonSign || hasNoZeroPrefixNumbers || selected.all}
                    getList={getSelectedList}
                    pr_key={pr_key}
                    service={service}
                    update={() => {
                        updateRanges();
                        getNumberList(unitedFilters, 1, numbersPerPage, sort);
                        tableRef.current.clear();
                    }}
                    isSetTest={true}
                />
            </FlexboxGrid.Item>}
            {showUnsetTestButton && <FlexboxGrid.Item>
                <SetOrUnsetTest
                    incorporatedOnly={incorporatedOnly}
                    selectedNumbersInfo={selectedNumbersInfo}
                    disabled={!hasSelected || disabledBlockedNumbersButtonSign || selected.all}
                    getList={getSelectedList}
                    pr_key={pr_key}
                    service={service}
                    update={() => {
                        updateRanges();
                        getNumberList(unitedFilters, 1, numbersPerPage, sort);
                        tableRef.current.clear();
                    }}
                    isSetTest={false}
                />
            </FlexboxGrid.Item>}
            {showBlockButton && <FlexboxGrid.Item>
                <Block
                    incorporatedOnly={incorporatedOnly}
                    selectedNumbersInfo={selectedNumbersInfo}
                    disabled={!hasSelected || disabledBlockedNumbersButtonSign || selected.all}
                    getList={getSelectedList}
                    pr_key={pr_key}
                    service={service}
                    update={() => {
                        updateRanges();
                        getNumberList(unitedFilters, 1, numbersPerPage, sort);
                        tableRef.current.clear();
                    }}
                />
            </FlexboxGrid.Item>}
            {showUnblockButton && <FlexboxGrid.Item>
                <Unblock
                    incorporatedOnly={incorporatedOnly}
                    selectedNumbersInfo={selectedNumbersInfo}
                    disabled={!hasSelected || selected.list.length && !disabledBlockedNumbersButtonSign || selected.all}
                    getList={getSelectedList}
                    pr_key={pr_key}

                    service={service}
                    update={() => {
                        updateRanges();
                        getNumberList(unitedFilters, 1, numbersPerPage, sort);
                        tableRef.current.clear();
                    }}
                />
            </FlexboxGrid.Item>}
        </FlexboxGrid>
        <div style={{ marginLeft: 20 }}>
            <Filters
                value={filters}
                onChange={onChangeFilters}
            />
            <TableServerSort
                ref={tableRef}
                data={numbers}
                loading={numbersLoading}
                columns={columns}
                originalColumns={columns}
                count={numbersCount}
                per_page={numbersPerPage}
                page={numbersPage}
                getItems={(page, perPage) => {
                    getNumberList(unitedFilters, page, perPage, sort);
                }}
                row_key="prn_key"
                isSelectedAll={!incorporatedOnly}
                setSelected={selected => {
                    const filteredNumbers = selectedNumbersInfo.filter(item => selected.list.includes(item.prn_key));
                    const currentPageNumbers = numbers.filter(item => selected.list.includes(item.prn_key));
                    const numbersUpdated = currentPageNumbers.reduce((summ, item) => {
                        if (summ.find(_item => _item.prn_key === item.prn_key)) {
                            return summ;
                        }
                        return [...summ, item];
                    }, [...filteredNumbers]);

                    setSelected(selected);
                    setSelectedNumbersInfo(numbersUpdated);
                }}
                ispagination
                isselected
                onSort={(column, type) => {
                    onSort({ column, type });
                }}
                perPageLSKey={LS_RANGES_AND_NUMBERS_NUMBERS_PER_PAGE}
            />
        </div>
    </>);
});

const StyledTooltipIcon = styled(Icon)`
    margin-left: 5px;

    .rtl & {
        margin-left: 0;
        margin-right: 5px;
    }
`;

const StyledTooltipInner = styled.div`
    text-align: left;
`;

const AdditionalInfo = styled.span`
    && {
        color: gray;
    }
`;